import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useRecoilState, useSetRecoilState } from "recoil";

import CancelButton from "../../../../../components/CancelButton";
import ConfirmBoxModal from "../../../../../components/ConfirmBoxModal";
import SubmitHookForm from "../../../../../components/SubmitHookForm";
import Timer from "../../Timer";
import {
  addStateSalesLead,
  editStateSalesLead,
  rowEditSalesLead,
  salesLeadFetching,
  savingSalesLead,
} from "../atoms/SalesLeadAtoms";
import useAddSalesLead from "../hooks/useAddSalesLead";
import useUpdateSalesLead from "../hooks/useUpdateSalesLead";
import { SalesLeadData } from "../types/SalesLeadInterface";
import SalesLeadDetails from "./SalesLeadDetails";

export default function SalesLeadForm(props: { refetch: () => void }) {
  // Drilled Props
  const { refetch } = props;

  // Atom States
  const [isAddState, setIsAddState] = useRecoilState(addStateSalesLead);
  const [actionEditState, setActionEditState] =
    useRecoilState(editStateSalesLead);
  const [isSaving, setIsSaving] = useRecoilState(savingSalesLead);
  const [editData, setEditData] = useRecoilState(rowEditSalesLead);
  const setIsFetching = useSetRecoilState(salesLeadFetching);

  // Local States
  const [newData, setNewData] = useState<SalesLeadData>();
  const [saveModal, setSaveModal] = useState(false);
  const [changes, setChanges] = useState(false);

  // Hook Functions
  const updateData = useUpdateSalesLead();
  const addData = useAddSalesLead();

  // Form Functions
  const useFormMethods = useForm({
    defaultValues: { ...editData },
  });

  const onSubmit = (data) => {
    const value = {
      ...data,
      name: data.name.trim(),
    };
    setNewData(value);
    setSaveModal(true);
    setChanges(false);
    setIsSaving(true);
  };

  const handleCancel = () => {
    useFormMethods.reset();
    useFormMethods.clearErrors();
    setEditData(undefined);
    setIsAddState(false);
    setActionEditState(false);
  };

  const handleSave = async () => {
    if (newData) {
      if (actionEditState) {
        updateData(newData);
      } else {
        addData(newData);
      }
    }
    setIsFetching(true);
    useFormMethods.reset();
    useFormMethods.clearErrors();
    setEditData(undefined);
    setIsAddState(false);
    setActionEditState(false);
    setIsSaving(false);
    await Timer();

    refetch();
  };

  return (
    <>
      <div className="px-2 py-2 space-y-2 border-l-2 border-primary box-shadow">
        <FormProvider {...useFormMethods}>
          <form onSubmit={useFormMethods.handleSubmit(onSubmit)}>
            <div className="flex flex-row  justify-between items-center text-quinary font-bold font-sans text-lg pb-1">
              <div className="ml-2 font-bold text-primary text-base">
                {isAddState ? "Add New Sales Lead" : "Edit Sales Lead"}
              </div>
              <div className="flex justify-between gap-2 mr-2">
                <SubmitHookForm
                  type="submit"
                  disabled={!changes}
                  buttonText={isSaving ? "Saving.." : "Save"}
                  className={undefined}
                  onClick={undefined}
                />
                <CancelButton
                  onClick={(event) => {
                    event.preventDefault();
                    handleCancel();
                  }}
                />
              </div>
            </div>
            <div className="mx-2 border-b-2 border-secondary"></div>
            <div className="text-xs text-secondary italic font-normal mt-2 ml-2">
              <b> Note:</b> All fields with * are required to fill.
            </div>
          </form>
          <div>
            <SalesLeadDetails data={editData} setChanges={setChanges} />
          </div>
        </FormProvider>
      </div>

      {saveModal && (
        <ConfirmBoxModal
          ModalTitle={isAddState ? "Add Sales Lead" : "Update Sales Lead"}
          qConfirmation={`Are you sure you want to ${
            isAddState ? "add " : "update "
          } this?`}
          isOpen={saveModal}
          onClose={() => {
            setSaveModal(false);
            setIsSaving(false);
            setChanges(true);
          }}
          onClickFunction={() => {
            setSaveModal(false);
            handleSave();
          }}
          cancelButtonRef={undefined}
        />
      )}
    </>
  );
}
